import { MapPin, ZoomIn, ZoomOut } from 'lucide-react';
import { useId, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import type { GmfMapProject } from '../types/gmfDeveloperPipeline.types';
import { formatNumber } from '../utils/formatters';

interface MapWidgetProps {
  projects: GmfMapProject[];
}

export const MapWidget = ({ projects }: MapWidgetProps) => {
  const [zoom, setZoom] = useState(12);
  const gridPatternId = useId();

  return (
    <Card className='h-full rounded-lg py-0'>
      <CardHeader className='p-6'>
        <CardTitle className='flex items-center gap-2 text-base'>
          <MapPin className='size-4 text-primary' />
          Milwaukee Project Map
        </CardTitle>
      </CardHeader>
      <CardContent className='px-6 pb-6'>
        <div className='relative h-[500px] overflow-hidden rounded-lg border border-border bg-muted'>
          <svg className='h-full w-full' viewBox='0 0 600 500' role='img' aria-label='Milwaukee project locations'>
            <defs>
              <pattern id={gridPatternId} width='40' height='40' patternUnits='userSpaceOnUse'>
                <path d='M 40 0 L 0 0 0 40' fill='none' stroke='currentColor' strokeWidth='0.5' className='text-border' opacity='0.55' />
              </pattern>
            </defs>
            <rect width='600' height='500' fill={`url(#${gridPatternId})`} />
            <path
              d='M 160 80 L 430 95 L 475 190 L 420 410 L 178 390 L 125 225 Z'
              fill='var(--card)'
              stroke='var(--muted-foreground)'
              strokeWidth='2'
              opacity='0.55'
            />
            <text x='170' y='118' className='fill-muted-foreground text-xs'>
              Milwaukee
            </text>
            {projects.slice(0, 14).map((project) => (
              <g key={project.id} className='group cursor-pointer'>
                <circle cx={project.x} cy={project.y} r='9' fill='var(--primary)' stroke='var(--primary-foreground)' strokeWidth='2' />
                <circle
                  cx={project.x}
                  cy={project.y}
                  r='16'
                  fill='var(--primary)'
                  opacity='0.16'
                  className='transition-opacity group-hover:opacity-30'
                />
                <g className='opacity-0 transition-opacity group-hover:opacity-100'>
                  <rect x={project.x - 78} y={project.y - 62} width='156' height='48' rx='8' fill='var(--popover)' stroke='var(--border)' />
                  <text x={project.x} y={project.y - 42} textAnchor='middle' className='fill-foreground text-xs font-medium'>
                    {project.name.slice(0, 22)}
                  </text>
                  <text x={project.x} y={project.y - 25} textAnchor='middle' className='fill-muted-foreground text-xs'>
                    {formatNumber(project.units)} units
                  </text>
                </g>
              </g>
            ))}
          </svg>

          {projects.length === 0 && (
            <div className='absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground'>
              No submitted project locations yet.
            </div>
          )}

          <div className='absolute left-4 top-4 flex items-center gap-2 rounded-lg border border-border bg-card/95 px-3 py-2 text-sm shadow-sm backdrop-blur-sm'>
            <MapPin className='size-4 text-primary' />
            <span className='font-medium'>{formatNumber(projects.length)} Active Projects</span>
          </div>
          <div className='absolute bottom-4 right-4 flex flex-col gap-2'>
            <button
              type='button'
              onClick={() => setZoom((value) => Math.min(value + 1, 16))}
              className='cursor-pointer rounded-lg border border-border bg-card p-2 shadow-sm transition-colors hover:bg-accent'
              aria-label='Zoom in'
            >
              <ZoomIn className='size-4' />
            </button>
            <button
              type='button'
              onClick={() => setZoom((value) => Math.max(value - 1, 8))}
              className='cursor-pointer rounded-lg border border-border bg-card p-2 shadow-sm transition-colors hover:bg-accent'
              aria-label='Zoom out'
            >
              <ZoomOut className='size-4' />
            </button>
          </div>
          <div className='absolute bottom-4 left-4 rounded-lg border border-border bg-card/95 px-3 py-2 text-xs text-muted-foreground shadow-sm'>
            Zoom {zoom}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
